import { Close, Info } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  Grid,
  IconButton,
  Stack,
} from '@mui/material';
import {
  startSignProcess,
  updatePortfolio,
} from 'lib/api/integrations/digitalSignature';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { Portfolio, PortfolioStatus } from 'lib/types/models';
import React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import Documents from './Documents';
import GeneralInfo from './GeneralInfo';
import InvalidateDialog from './InvalidateDialog';
import Signers from './Signers';
import Validations from './Validations';

export interface PortfolioModalProps {
  open: boolean;
  onClose: () => void;
  portfolio: Portfolio | null;
  onPortfolioUpdated?: () => void;
  pruffConnect?: boolean;
}

const PortfolioModal: React.FC<PortfolioModalProps> = ({
  open,
  onClose,
  portfolio,
  onPortfolioUpdated,
  pruffConnect = false,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showInvalidateDialog, setShowInvalidateDialog] = useState(false);

  const handleStartSignature = async () => {
    try {
      setLoading(true);
      if (!portfolio?.id) {
        showAlert(dispatch, 'error', 'Portafolio no encontrado');
        return;
      }
      await startSignProcess(dispatch, portfolio.id);
      showAlert(dispatch, 'success', 'Firma iniciada exitosamente');
      onPortfolioUpdated?.();
      onClose();
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvalidatePortfolio = async () => {
    try {
      setLoading(true);
      if (!portfolio?.id) {
        showAlert(dispatch, 'error', 'Portafolio no encontrado');
        return;
      }
      await updatePortfolio(dispatch, portfolio.id, {
        status: PortfolioStatus.INVALIDATED,
      });
      showAlert(dispatch, 'success', 'Portafolio invalidado exitosamente');
      setShowInvalidateDialog(false);
      onPortfolioUpdated?.();
      onClose();
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvalidateClick = () => {
    setShowInvalidateDialog(true);
  };

  const handleCancelInvalidate = () => {
    setShowInvalidateDialog(false);
  };

  if (!portfolio) return null;

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, pt: 1.5, pb: 1 }}
        >
          <Title
            title={portfolio.title ?? 'Detalle de Portafolio'}
            icon={<Info color="primary" />}
            color="primary"
          />
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              backgroundColor: 'rgba(0,0,0,0.04)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
            }}
          >
            <Close />
          </IconButton>
        </Stack>

        <Box sx={{ px: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <GeneralInfo portfolio={portfolio} />
              <Signers portfolio={portfolio} />
              {!pruffConnect && (
                <Validations
                  portfolio={portfolio}
                  onPortfolioUpdated={onPortfolioUpdated}
                />
              )}
            </Grid>

            <Grid item xs={6}>
              <Documents portfolio={portfolio} />
            </Grid>
          </Grid>
        </Box>
        {!pruffConnect && (
          <DialogActions>
            {portfolio.status === 'Completado' && (
              <CustomButton color="error" onClick={handleInvalidateClick}>
                Invalidar
              </CustomButton>
            )}
            {portfolio.status === 'Borrador' && (
              <CustomButton color="primary" onClick={handleStartSignature}>
                Comenzar Firma
              </CustomButton>
            )}
          </DialogActions>
        )}
      </Dialog>
      <InvalidateDialog
        open={showInvalidateDialog}
        onClose={handleCancelInvalidate}
        portfolio={portfolio}
        handleInvalidatePortfolio={handleInvalidatePortfolio}
        handleCancelInvalidate={handleCancelInvalidate}
      />
    </>
  );
};

export default PortfolioModal;
